import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, EmptyState } from "../components/common";
import { BookOpen, Search, X, FileText, Filter, ChevronRight, Loader2 } from "lucide-react";

/**
 * LearnPage — reads the Marianne Knowledge Base (parts 27–48) that has been
 * ingested at server start by services/kb_ingest.py into the
 * ``knowledge_entries`` collection.
 */
export default function LearnPage() {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [activeFull, setActiveFull] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState({ category: "", entry_type: "" });
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null); // search results

  // Load stats
  useEffect(() => { api.get("/knowledge/stats").then((r) => setStats(r.data)); }, []);

  // Load browse list when filters change
  useEffect(() => {
    if (results) return; // search mode owns the list
    setLoading(true);
    const params = new URLSearchParams();
    if (filter.category) params.set("category", filter.category);
    if (filter.entry_type) params.set("entry_type", filter.entry_type);
    params.set("limit", "100");
    api.get(`/knowledge/entries?${params.toString()}`)
      .then((r) => setItems(r.data.items || []))
      .finally(() => setLoading(false));
  }, [filter, results]);

  // Lazy-load full entry text when selected
  useEffect(() => {
    if (!active) { setActiveFull(null); return; }
    setActiveFull(null);
    api.get(`/knowledge/entries/${encodeURIComponent(active.id)}`)
      .then((r) => setActiveFull(r.data));
  }, [active]);

  const runSearch = async () => {
    const query = q.trim();
    if (!query) { setResults(null); return; }
    setLoading(true);
    try {
      const r = await api.get(`/knowledge/search?q=${encodeURIComponent(query)}&limit=30`);
      setResults(r.data.results || []);
      setItems((r.data.results || []).map((h) => ({ ...h })));
      setActive(null);
    } finally { setLoading(false); }
  };

  const clearSearch = () => { setQ(""); setResults(null); setActive(null); };

  const categories = stats?.categories || [];
  const entryTypes = stats?.entry_types || [];

  const activeCount = results ? results.length : items.length;

  return (
    <div data-testid="learn-page">
      <PageHeader
        eyebrow="La Connaissance"
        title="Learn"
        description={
          stats?.total
            ? `A searchable library of ${stats.total.toLocaleString()} entries drawn from the Marianne Knowledge Base — concepts, workflows, policies, data models and operational playbooks.`
            : "Loading the Marianne Knowledge Base…"
        }
      />

      {/* Search */}
      <div className="mb-6 flex gap-2">
        <div className="flex-1 flex items-center border border-[#E8E3F0] bg-white px-3">
          <Search size={16} className="text-[#4A5568]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runSearch()}
            placeholder="Search the Knowledge Base — e.g. consumer complaint, witness, dispute resolution…"
            className="flex-1 px-3 py-2.5 text-sm bg-transparent focus:outline-none"
            data-testid="learn-search-input"
          />
          {q && (
            <button onClick={clearSearch} className="text-[#4A5568] hover:text-[#2A0F5A]" data-testid="learn-search-clear">
              <X size={14} />
            </button>
          )}
        </div>
        <button
          onClick={runSearch}
          className="px-5 py-2.5 bg-[#2A0F5A] text-[#FAFAF8] text-xs tracking-[0.18em] uppercase hover:bg-[#4A2680]"
          data-testid="learn-search-btn"
        >
          Search
        </button>
      </div>

      {/* Filter chips */}
      {!results && (
        <div className="mb-6 flex flex-wrap gap-2 items-center">
          <span className="label-eyebrow mr-1 flex items-center gap-1.5"><Filter size={11} /> Category</span>
          <button
            onClick={() => setFilter((f) => ({ ...f, category: "" }))}
            className={`px-2.5 py-1 text-[10px] tracking-[0.18em] uppercase border ${filter.category === "" ? "bg-[#2A0F5A] text-[#FAFAF8] border-[#2A0F5A]" : "bg-white border-[#E8E3F0] hover:border-[#7B56C9]"}`}
          >All</button>
          {categories.slice(0, 10).map((c) => (
            <button
              key={c.category}
              onClick={() => setFilter((f) => ({ ...f, category: c.category === f.category ? "" : c.category }))}
              className={`px-2.5 py-1 text-[10px] tracking-[0.18em] uppercase border ${filter.category === c.category ? "bg-[#2A0F5A] text-[#FAFAF8] border-[#2A0F5A]" : "bg-white border-[#E8E3F0] hover:border-[#7B56C9]"}`}
              data-testid={`learn-cat-${c.category.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              {c.category} <span className="text-[#4A5568] ml-1 normal-case tracking-normal">({c.count})</span>
            </button>
          ))}
        </div>
      )}

      {!results && entryTypes.length > 0 && (
        <div className="mb-6 flex flex-wrap gap-2 items-center">
          <span className="label-eyebrow mr-1">Type</span>
          <button
            onClick={() => setFilter((f) => ({ ...f, entry_type: "" }))}
            className={`px-2.5 py-1 text-[10px] tracking-[0.18em] uppercase border ${filter.entry_type === "" ? "bg-[#C5A059] text-white border-[#C5A059]" : "bg-white border-[#E8E3F0] hover:border-[#C5A059]"}`}
          >All</button>
          {entryTypes.map((t) => (
            <button
              key={t.entry_type}
              onClick={() => setFilter((f) => ({ ...f, entry_type: t.entry_type === f.entry_type ? "" : t.entry_type }))}
              className={`px-2.5 py-1 text-[10px] tracking-[0.18em] uppercase border ${filter.entry_type === t.entry_type ? "bg-[#C5A059] text-white border-[#C5A059]" : "bg-white border-[#E8E3F0] hover:border-[#C5A059]"}`}
            >
              {t.entry_type} <span className="opacity-70 ml-1 normal-case tracking-normal">({t.count})</span>
            </button>
          ))}
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-6">
        {/* List pane */}
        <div className="md:col-span-1 border border-[#E8E3F0] bg-white max-h-[75vh] flex flex-col">
          <div className="px-5 py-3 border-b border-[#E8E3F0] label-eyebrow flex items-center justify-between">
            <span>{results ? `${activeCount} search ${activeCount === 1 ? "result" : "results"}` : `${activeCount} ${activeCount === 1 ? "entry" : "entries"}`}</span>
            {loading && <Loader2 size={12} className="animate-spin" />}
          </div>
          <ul className="divide-y divide-[#E8E3F0] overflow-y-auto flex-1">
            {items.length === 0 && !loading && (
              <li className="px-5 py-12 text-center text-xs text-[#4A5568]">
                {results ? "No results — try a different search." : "No entries match these filters."}
              </li>
            )}
            {items.map((e) => (
              <li key={e.id}>
                <button
                  onClick={() => setActive(e)}
                  className={`w-full text-left px-5 py-3 hover:bg-[#FAFAF8] group ${active?.id === e.id ? "bg-[#FAFAF8]" : ""}`}
                  data-testid={`kb-entry-${e.id}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="label-eyebrow text-[#C5A059]">{e.category}</span>
                    <span className="text-[9px] uppercase tracking-widest text-[#4A5568]">{e.entry_type}</span>
                  </div>
                  <div className="font-serif text-base leading-tight">{e.topic}</div>
                  {e.snippet && (
                    <div className="text-xs text-[#4A5568] mt-1.5 line-clamp-2 italic">{e.snippet}</div>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Reading pane */}
        <div className="md:col-span-2 border border-[#E8E3F0] bg-white p-6 md:p-8 min-h-[60vh]">
          {!active ? (
            <EmptyState
              icon={BookOpen}
              title="Select an entry"
              description="Pick an item on the left to read its full text — concept, workflow, policy, specification, data model, UX pattern, test case, or operational playbook."
            />
          ) : !activeFull ? (
            <div className="flex items-center justify-center min-h-[40vh] text-[#4A5568]">
              <Loader2 size={16} className="animate-spin mr-2" /> Opening entry…
            </div>
          ) : (
            <article data-testid="kb-entry-detail">
              <div className="flex items-center gap-3 mb-2">
                <span className="label-eyebrow text-[#C5A059]">{activeFull.category}</span>
                <span className="text-[10px] tracking-widest uppercase text-[#4A5568] border border-[#E8E3F0] px-2 py-0.5">{activeFull.entry_type}</span>
              </div>
              <h2 className="font-serif text-3xl md:text-4xl tracking-tight mb-3">{activeFull.topic}</h2>
              <div className="text-xs text-[#4A5568] mb-6 flex items-center gap-3">
                <span>Source: Marianne Knowledge Base · Part {activeFull.source_part} · p. {activeFull.source_page}</span>
                <span>·</span>
                <span>{activeFull.length?.toLocaleString()} chars</span>
              </div>
              <div className="prose max-w-none text-[15px] leading-[1.75] whitespace-pre-wrap text-[#2A0F5A]/90 font-sans">
                {activeFull.text}
              </div>
              <div className="mt-10 pt-6 border-t border-[#E8E3F0] text-xs text-[#4A5568]">
                This content is drawn from the Marianne Knowledge Base. It is reference material to help you understand concepts and procedures — it is not legal advice.
              </div>
            </article>
          )}
        </div>
      </div>
    </div>
  );
}
